import { Box, Card, Container, Grid, Hidden, IconButton, makeStyles, Theme, Typography } from '@material-ui/core';
import { RadioGroupComponent } from 'components/atoms';
import { followUsIconLinks } from 'config/routes';
import { useMediaUp } from 'hooks';
import { contactUsFormBg } from 'images/contact-us';
import { FC, useState } from 'react';
import { BlackDotsIcon, MultiLinesIcon, PlusIcon, RedCirclePointedIcon } from 'svgs';
import { QuoteForm } from 'templates/forms';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    backgroundImage: 'none',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    [theme.breakpoints.up('md')]: {
      backgroundImage: `url(${contactUsFormBg})`,
    },
  },
  formCard: {
    padding: theme.spacing(10, 6),
    position: 'relative',
    width: '100%',
    background: theme.palette.switchedColors.whiteGray,
    overflow: 'visible',
    boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      background: theme.palette.switchedColors.whiteGray,
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(10),
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(19),
    },
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(9),
    fontSize: 32,
    '& svg': {
      marginTop: theme.spacing(1),
      maxWidth: '100%',
      width: 132,
      height: 19,
    },
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(5),
      fontSize: 30,
    },
    [theme.breakpoints.up('lg')]: {
      fontSize: 36,
    },
  },
  infoWrapper: {
    padding: theme.spacing(14, 3),
    background: theme.palette.primary.dark,
    color: theme.palette.common.white,
    borderRadius: theme.spacing(4),
    marginBottom: theme.spacing(10),
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(18, 10),
    },
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(0),
      borderRadius: theme.spacing(4, 0, 0, 4),
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(18, 20),
    },
  },
  submitButton: {
    marginTop: theme.spacing(10),
  },
  whiteText: {
    color: theme.palette.common.white,
  },
  infoText: {
    margin: theme.spacing(3, 0, 6),
  },
  logoLink: {
    margin: theme.spacing(2, 3, 0, 0),
    background: theme.palette.primary.light,
    '& path': {
      fill: 'white',
    },
  },
  redCircleIcon: {
    position: 'absolute',
    left: theme.spacing(4),
    bottom: theme.spacing(4),
    zIndex: 0,
    maxWidth: '100%',
    height: 70,
    width: 70,
  },
  plusIcon: {
    maxWidth: '100%',
    position: 'absolute',
    top: '10%',
    right: '17%',
    width: 28,
    height: 28,
  },
  blackDotsIcon: {
    position: 'absolute',
    zIndex: -1,
    top: theme.spacing(-6),
    left: theme.spacing(-6),
    [theme.breakpoints.up('sm')]: {
      top: theme.spacing(-12),
      left: theme.spacing(-6),
    },
  },
  greenSquareIcon: {
    width: 220,
    height: 220,
    backgroundColor: theme.palette.primary.main,
    position: 'absolute',
    zIndex: -1,
    bottom: theme.spacing(-6),
    right: theme.spacing(-6),
  },
}));

const preferredMethodValues = ['Email', 'Phone'];

const ContactForm: FC = () => {
  const classes = useStyles();
  const [preferredMethod, setPreferredMethod] = useState(preferredMethodValues[0]);
  const isSmall = useMediaUp('sm');
  return (
    <Box py={isSmall ? 40 : 28} className={classes.container}>
      <Container fixed>
        <Grid alignItems="center" container>
          <Grid md={6} xs={12} item>
            <Box className={classes.infoWrapper}>
              <Hidden smDown>
                <RedCirclePointedIcon viewBox="0 0 130 108" className={classes.redCircleIcon} />
                <PlusIcon viewBox="0 0 40 40" className={classes.plusIcon} />
              </Hidden>
              <Typography variant="h3" className={classes.whiteText}>
                Get in Touch
              </Typography>
              <Typography className={classes.infoText}>
                We are a few clicks away for all inquiries, please feel free to get in touch using the form below
              </Typography>
              <Typography variant="h4" className={classes.whiteText}>
                Subscribe to our Social Media:
              </Typography>
              {followUsIconLinks.map(({ link, logo }, index) => (
                <a href={link} key={index} target="_blank" rel="noopener noreferrer">
                  <IconButton color="primary" className={classes.logoLink}>
                    {logo}
                  </IconButton>
                </a>
              ))}
            </Box>
          </Grid>
          <Grid md={6} xs={12} item>
            <Card className={classes.formCard}>
              <BlackDotsIcon className={classes.blackDotsIcon} />
              <Box className={classes.greenSquareIcon} />
              <Typography variant="h3" className={classes.cardTitle}>
                Reach out to us <MultiLinesIcon viewBox="0 0 59 11" />
              </Typography>
              <QuoteForm
                light
                buttonProps={{ className: classes.submitButton, fullWidth: true }}
                externalFields={
                  <RadioGroupComponent
                    title="Preferred method of communication"
                    onChange={setPreferredMethod}
                    values={preferredMethodValues}
                    value={preferredMethod}
                  />
                }
                externalValues={{ preferredMethod }}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactForm;
